import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, CameraResultType, Photo } from '@capacitor/camera';
import axiosClient from '../api/axiosClient';
import { canQueueMessage } from '../api/offlineQueue';
import { useWebSocket } from './useWebSocket';
import {
    getMessageConversationId,
    getSenderId,
    isIncomingMessage,
    isSystemEvent,
} from '../api/wsMessage';
import { Conversation, Message } from '../types';

export const useChat = (id: string | undefined, currentUser: any) => {
    const { latestMessage, socket } = useWebSocket(id, currentUser?.id);
    const [conversation, setConversation] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessageText, setNewMessageText] = useState('');
    const [isContactOnline, setIsContactOnline] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [chatPartner, setChatPartner] = useState<any>(null);
    const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);


    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    // id сообщений, которые уже переотправляются: защита от двойного клика.
    const retryingIdsRef = useRef<Set<string>>(new Set());

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const fetchChat = useCallback(async () => {
        if (!id) return;
        
        // МГНОВЕННЫЙ СБРОС (Броня против старых данных)
        setMessages([]);
        setConversation(null);
        setHasMore(true);
        setError(null);
        try {
            const [convRes, msgRes] = await Promise.all([
                axiosClient.get<Conversation>(`/conversations/${id}`),
                axiosClient.get<Message[]>(`/conversations/${id}/messages`)
            ]);
            setConversation(convRes.data);
            setMessages(msgRes.data);
            // Если пришло меньше 20, значит истории больше нет
            if (msgRes.data.length < 20) setHasMore(false);

            setTimeout(scrollToBottom, 100);
        } catch (err: any) { 
            if (err.response?.status === 403) {
                setError('Access Denied');
            } else {
                setError('Ошибка загрузки чата');
            }
            console.error(err); 

        }
    }, [id, scrollToBottom]);

    //подгрузка старых сообщений

    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // ПОДГРУЗКА СТАРЫХ СООБЩЕНИЙ
    const loadMore = useCallback(async () => {
        // Не грузим, если: уже грузим, нет истории, список пуст или нет ID
        if (loadingMore || !hasMore || messages.length === 0 || !id) return;

        setLoadingMore(true);
        try {
            const firstMsgId = messages[0].id; // Берем ID самого ВЕРХНЕГО сообщения
            const res = await axiosClient.get<Message[]>(`/conversations/${id}/messages?beforeId=${firstMsgId}`);
            
            if (res.data.length === 0) {
                setHasMore(false);
            } else {
                if (res.data.length < 20) setHasMore(false);
                
                // 🛡️ Доклеиваем старые сообщения В НАЧАЛО
                setMessages(prev => [...res.data, ...prev]);
            }
        } catch (err) {
            console.error("Load more failed", err);
        } finally {
            setLoadingMore(false);
        }
    }, [id, messages, hasMore, loadingMore]);


    // Загрузка чата
    useEffect(() => {
        if (id) {
            fetchChat();
            axiosClient.post(`/conversations/${id}/read`).catch(() => {});
        }
    }, [id, fetchChat]);


    // Синхронизация при загрузке
    useEffect(() => {
        if (conversation?.contact) {
            setChatPartner(conversation.contact);
            setIsContactOnline(!!conversation.contact.isOnline);
        }
    }, [conversation]);


    // Обработка входящих событий (сокетов)

    useEffect(() => {
        if (!latestMessage) return;
        // 1. РАСПАКОВКА И ПОИСК ID БЕСЕДЫ
        // Бэкенд шлёт ID чата в корне, а данные сообщения в payload.
        // Если это системное событие (event), используем его как есть.
        const isEvent = isSystemEvent(latestMessage);
        const incomingConvId = String(getMessageConversationId(latestMessage) || '').toLowerCase();
        const currentChatId = String(id).toLowerCase();

        // Если это не событие И чаты не совпали — игнорируем
        if (!isEvent && incomingConvId !== '' && incomingConvId !== currentChatId) {
            return; 
        }

        // 2. ОБРАБОТКА СИСТЕМНЫХ СОБЫТИЙ (Status / Typing)
        if (isEvent) {
            const eventType = latestMessage.event;
            const socketUserId = String(latestMessage.userId || '').toLowerCase();
            const currentUserId = String(currentUser?.id || '').toLowerCase();

            // 1. Статус Online (Выходим сразу, если юзер не тот)
            if (eventType === 'userStatusChanged') {
                const partnerId = String(conversation?.contact?.id || chatPartner?.id || '').toLowerCase();
                if (socketUserId === partnerId && partnerId !== '') {
                    const isOnline = latestMessage.status === 'online';
                    setIsContactOnline(isOnline);
                    setChatPartner((prev: any) => prev ? ({ ...prev, isOnline, lastSeen: latestMessage.lastSeen }) : prev);
                }
            }

            // 2. Typing (Разрешаем для текущего чата или пустого ID из ТГ)
            const isTypingEvent = eventType === 'typing';
            const isCorrectChat = incomingConvId === currentChatId || incomingConvId === '';
            const isNotMe = socketUserId !== currentUserId;

            if (isTypingEvent && isCorrectChat && isNotMe) {
                setIsTyping(true);
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
            }
            return; 
        }

        // 3. ОБРАБОТКА СООБЩЕНИЙ
        setIsTyping(false);
        
        setMessages(prev => {
            // 🛡️ 1. БЕРЕМ ДАННЫЕ (уже без матрешек)
            const msg = latestMessage; 
            
            // Определяем автора
            const isMe = getSenderId(msg) === String(currentUser?.id || '').toLowerCase();

            // 🛡️ 2. ФИКС ЗАДВОЕНИЯ: Если это МОЕ сообщение из сокета — игнорируем.
            if (isMe) return prev;

            // 🛡️ 3. ПРОВЕРКА ПО ID (для чужих)
            if (prev.some(m => m.id === msg.id)) return prev;

            // 🛡️ 4. ПРИВОДИМ К ЕДИНОМУ ВИДУ
            // Направление берём из самого сообщения: ответ коллеги из другой
            // организации — не «входящее от клиента», и звук по нему не нужен.
            const correctedMessage = {
                ...msg,
                direction: isIncomingMessage(msg, currentUser?.id) ? 'incoming' : 'outgoing',
                sentAt: msg.sentAt || new Date().toISOString(),
                // Пробрасываем payload как есть, там уже лежат replyTo и имена
                payload: msg.payload || {}
            };

            return [...prev, correctedMessage as Message];
        });

        // Скроллим вниз только если пришло РЕАЛЬНОЕ новое сообщение по сокету.
        // Мы НЕ вызываем это при loadMore, так как loadMore работает в другом месте.
        if (!isEvent) {
            setTimeout(scrollToBottom, 50);
        }
    }, [latestMessage, conversation, chatPartner?.id, id, currentUser?.id, scrollToBottom]);




    //Логика ОТПРАВКИ статуса "Печатаю"
    useEffect(() => {
        if (!socket || !id || !newMessageText.trim()) return;

        // Сообщаем серверу, что мы печатаем в этой беседе
        socket.emit('typing', {
            conversationId: id,
            userId: currentUser?.id
        });
    }, [newMessageText, id, socket, currentUser?.id]);



    const [replyTo, setReplyTo] = useState<Message | null>(null); // Состояние для цитаты

    // Добавляем аргумент text
    const handleSend = async (text: string) => {
        // Проверяем пришедший текст и наличие id беседы
        if (!text.trim() || !id) return;

        const currentReply = replyTo; // Фиксируем текущую цитату (она остается в стейте, это ок)

        // ВАЖНО: Больше не вызываем setNewMessageText(''),
        // так как MessageInput сам очистит свой реф после вызова этой функции.
        setReplyTo(null); // Сбрасываем превью цитаты над инпутом

        const tempId = `temp-${Date.now()}`;
        const newMessage = {
            id: tempId,
            text: text, // Используем аргумент
            direction: 'outbound',
            // Пока идёт POST — «отправляется» (часы в подвале пузыря).
            status: 'sending',
            sentAt: new Date().toISOString(),
            conversationId: id,
            isRead: false,
            senderType: 'user',
            payload: {
                senderId: currentUser?.id,
                replyTo: currentReply ? { id: currentReply.id, text: currentReply.text } : null
            }
        } as Message;

        // Оптимистичное обновление UI
        setMessages(prev => [...prev, newMessage]);
        setTimeout(scrollToBottom, 100);

        try {
            // Отправляем на бэкенд
            const res = await axiosClient.post(`/conversations/${id}/messages`, {
                text: text, // Используем аргумент
                replyToId: currentReply?.id
            });

            // Заменяем временный ID на реальный из базы
            setMessages(prev => prev.map(m => m.id === tempId
                ? { ...m, id: res.data.id, status: res.data.status || 'sent' }
                : m));
        } catch (err) {
            // Не теряем сообщение. Если ответа сервера нет, а service worker
            // поддерживает Background Sync — он сам повторит запрос, когда сеть
            // вернётся, поэтому помечаем «в очереди». Иначе текст остаётся
            // в пузыре и его можно переотправить кнопкой «Повторить».
            const queued = canQueueMessage(err);
            setMessages(prev => prev.map(m => m.id === tempId
                ? { ...m, status: queued ? 'queued' : 'failed' }
                : m));
            if (queued) {
                console.warn('Сообщение отложено в очередь service worker:', err);
            } else {
                console.error("Ошибка отправки:", err);
            }
        }
    };


    // Повторная отправка «не отправленного» сообщения (текст или фото).
    // Для фото берём исходный base64: он сохранён в payload временного
    // сообщения, а `preview` несёт data-URL для показа в пузыре.
    const retryMessage = async (message: Message) => {
        if (!id || !message?.id) return;
        if (retryingIdsRef.current.has(message.id)) return;
        retryingIdsRef.current.add(message.id);

        const messageId = message.id;
        const rawAttachment: string | undefined =
            (message.payload as any)?.attachmentBase64 ||
            (message.preview?.startsWith('data:') ? message.preview.split(',')[1] : undefined);

        setMessages(prev => prev.map(m => m.id === messageId
            ? { ...m, status: 'sending', isUploading: !!rawAttachment }
            : m));

        try {
            const res = await axiosClient.post(`/conversations/${id}/messages`, rawAttachment
                ? { text: message.text || '📷 Фото', attachment: rawAttachment }
                : { text: message.text, replyToId: (message.payload as any)?.replyTo?.id });

            setMessages(prev => prev.map(m => m.id === messageId
                ? { ...m, id: res.data.id, status: res.data.status || 'sent', isUploading: false, payload: res.data.payload || m.payload }
                : m));
        } catch (err) {
            // Сеть снова пропала, но очередь доступна — не пугаем пользователя
            // красной ошибкой, service worker повторит запрос сам.
            const queued = canQueueMessage(err);
            setMessages(prev => prev.map(m => m.id === messageId
                ? { ...m, status: queued ? 'queued' : 'failed', isUploading: false }
                : m));
            if (queued) {
                console.warn('Повтор отложен в очередь service worker:', err);
            } else {
                console.error("Повторная отправка не удалась:", err);
            }
        } finally {
            retryingIdsRef.current.delete(messageId);
        }
    };


    const takePhoto = async () => {
        if (!id) return;

        // Ошибка доступа к камере — это не «потерянное» сообщение: пузырь
        // не создаём, пользователь просто может попробовать снова.
        let image: Photo | null = null;
        try {
            image = await Camera.getPhoto({ quality: 90, resultType: CameraResultType.Base64, webUseInput: true });
        } catch (e) {
            console.warn('Не удалось открыть камеру:', e);
            return;
        }
        const attachment = image?.base64String;
        if (!attachment) return;

        const base64Data = `data:image/jpeg;base64,${attachment}`;
        const tempId = `temp-img-${Date.now()}`;
        setMessages(prev => [...prev, {
            id: tempId,
            text: "📷 Фото",
            direction: 'outbound',
            status: 'sending',
            sentAt: new Date().toISOString(),
            conversationId: id,
            preview: base64Data,
            isUploading: true,
            payload: { senderId: currentUser?.id, attachmentBase64: attachment }
        } as any]);
        setTimeout(scrollToBottom, 50);

        try {
            const res = await axiosClient.post(`/conversations/${id}/messages`, { text: "📷 Фото", attachment: attachment });
            setMessages(prev => prev.map(m => m.id === tempId
                ? { ...m, id: res.data.id, status: res.data.status || 'sent', isUploading: false, payload: res.data.payload || m.payload }
                : m));
        } catch (e) {
            // Гасим вечный спиннер. Если очередь доступна, service worker
            // отправит фото сам; иначе предлагаем «Повторить» — фото остаётся
            // видно по base64-превью и не теряется.
            const queued = canQueueMessage(e);
            console.warn(queued ? 'Фото отложено в очередь service worker:' : 'Не удалось загрузить фото:', e);
            setMessages(prev => prev.map(m => m.id === tempId
                ? { ...m, status: queued ? 'queued' : 'failed', isUploading: false }
                : m));
        }
    };

    return {
        conversation, messages, newMessageText, setNewMessageText, isContactOnline,
        isTyping, selectedImage, setSelectedImage, imageErrors, setImageErrors,
        messagesEndRef, chatPartner, takePhoto, handleSend, retryMessage, replyTo, setReplyTo, scrollToBottom, error, loadMore, hasMore, loadingMore, socket
    };
};
